import { Box, Button, HStack, Icon, Spinner, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiLock } from "react-icons/fi";
import {
  BidStatus,
  PortalInviteStatusDto,
  PortalLifecycleStatus,
  useAcceptPortalInviteMutation,
  useDeclinePortalInviteMutation,
  useGetPortalMyBidsQuery,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { isInvitedCompetition } from "../isInvitedCompetition";
import { readsWithoutAnInvitation } from "./readsWithoutAnInvitation";

export const isPrivatePortalEvent = (event: PortalSourcingEventDetailDto): boolean =>
  isInvitedCompetition(event.contextType);

export const PrivateInviteCta = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  // The server-persisted invite status is the source of truth; a successful accept/decline is
  // mirrored locally for instant feedback while the detail query refetches.
  const [localStatus, setLocalStatus] = useState<PortalInviteStatusDto | null>(null);
  const [accept, { isLoading: isAccepting }] = useAcceptPortalInviteMutation();
  const [decline, { isLoading: isDeclining }] = useDeclinePortalInviteMutation();
  const { data: myBids, isLoading: isLoadingMyBids } = useGetPortalMyBidsQuery(
    {},
    { skip: !isPrivatePortalEvent(event) },
  );

  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;
  const status = localStatus ?? event.myInviteStatus ?? PortalInviteStatusDto.NotInvited;

  const hasDeliveredBid =
    myBids?.bids.some((bid) => bid.eventId === event.id && bid.status !== BidStatus.Draft) ?? false;

  const notInvited = localStatus === null && readsWithoutAnInvitation(event);
  if (!isPrivatePortalEvent(event) || notInvited || !isOpen || isLoadingMyBids || hasDeliveredBid) return null;

  const handleAccept = async () => {
    try {
      await accept({ eventId: event.id }).unwrap();
      setLocalStatus(PortalInviteStatusDto.Accepted);
    } catch (error) {
      displayer.trigger(error);
    }
  };

  const handleDecline = async () => {
    try {
      await decline({ eventId: event.id }).unwrap();
      setLocalStatus(PortalInviteStatusDto.Declined);
    } catch (error) {
      displayer.trigger(error);
    }
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="xl" px={{ base: "4", md: "6" }} py="4">
      <HStack spacing="4" justify="space-between" align="center" flexWrap="wrap" rowGap="3">
        <HStack spacing="3" align="center">
          <Icon as={FiLock} boxSize="5" color="smPrimary" flexShrink="0" />
          <Stack spacing="0">
            <Text fontWeight="semibold">{t("You have been invited to this procurement")}</Text>
            <Text fontSize="sm" color="smMuted">
              {status === PortalInviteStatusDto.Declined
                ? t("You have declined this invitation.")
                : event.frameworkAgreementNoLongerActive
                  ? t("No bid can be delivered because the framework agreement is no longer active. You can decline the invitation.")
                  : t("Start your bid whenever you are ready, or decline if this is not for you.")}
            </Text>
          </Stack>
        </HStack>

        {isAccepting || isDeclining ? (
          <Spinner size="sm" color="smPrimary" flexShrink="0" />
        ) : status === PortalInviteStatusDto.Declined ? (
          <HStack spacing="3" flexShrink="0">
            <Text fontWeight="semibold" color="smMuted">
              {t("Invitation declined")}
            </Text>
            <Button variant="outline" colorScheme="gray" onClick={handleAccept} isLoading={isAccepting}>
              {t("Participate anyway")}
            </Button>
          </HStack>
        ) : (
          <Button variant="outline" colorScheme="gray" onClick={handleDecline} isLoading={isDeclining} flexShrink="0">
            {t("Decline")}
          </Button>
        )}
      </HStack>
    </Box>
  );
};
